import {
  getHtmlElement,
  getIframeElementWithDefaultStyles,
  listenMessageEvent,
  notifyConnectWithGatewayName,
  prepareMiddleIFrame,
} from '.';
import { getMiddleFrameURL } from '../constants';
import { IframePermissions, setPermissions } from './iframePermissions';
import PostMessageType from '../constants/postMessageTypes';
import { backupActiveElement, restoreActiveElement } from './activeElement';
import { GatewayError, UnknownError } from './customErrorTypes';
import errorMap from './errorMap';
import { store } from './userStore';
import windowTarget from './windowTarget';

const CONNECT_FRAME_LOAD_TIMEOUT_MS = 10_000;

let connectIframePromise: Promise<void> = null;

/**
 * Ensures that the connect frame is injected into the DOM and listens for messages.
 * If the connect frame is already injected, it returns the existing promise.
 * @returns A promise that resolves when the connect frame is loaded and ready.
 */
export const ensureConnectFrameInjected = async () => {
  if (connectIframePromise) return connectIframePromise;
  const middleFrameURL = getMiddleFrameURL(store.gateway);
  const middleFrameIframe = prepareMiddleIFrame(middleFrameURL);

  const referenceElement = getHtmlElement(windowTarget.elementOrSelector);
  referenceElement.appendChild(middleFrameIframe);

  const connectLoaded = listenMessageEvent(PostMessageType.CONNECT_LOADED);
  let loadTimeout: ReturnType<typeof setTimeout>;
  const timedOut = new Promise<never>((_, reject) => {
    loadTimeout = setTimeout(() => {
      connectLoaded.cancel();
      reject(
        new GatewayError(errorMap.API_ERROR, {
          cause: new UnknownError('Connect frame load timed out'),
        }),
      );
    }, CONNECT_FRAME_LOAD_TIMEOUT_MS);
  });

  connectIframePromise = Promise.race([
    connectLoaded.promise,
    timedOut,
  ]).then((event) => {
    clearTimeout(loadTimeout);
    notifyConnectWithGatewayName(store.displayName);
    store.thirdPartyCookieSupported = event.data.thirdPartyCookieSupported;

    window.addEventListener('message', (ev) => {
      // discard any message not sent by connect iframe
      if (ev.origin !== MIDDLE_FRAME_URL) return;

      if (ev.data.type === PostMessageType.SHOW_FRAME) {
        middleFrameIframe.style.visibility = 'unset';
        middleFrameIframe.style.display = 'unset';
        backupActiveElement();
        referenceElement.style.overflow = 'hidden';
      } else if (ev.data.type === PostMessageType.HIDE_FRAME) {
        middleFrameIframe.style.visibility = 'hidden';
        middleFrameIframe.style.display = 'none';
        restoreActiveElement();
        referenceElement.style.overflow = '';
      }
    });
  });
  return connectIframePromise;
};

/** create an iframe to embed a client url */
export const prepareGwClientIframe = () => {
  const iframeElement = getIframeElementWithDefaultStyles();
  iframeElement.style.zIndex = '+2147483647'; // safe highest allowed value
  iframeElement.classList.add('scdk-gw-client-frame');
  const referenceElement = getHtmlElement(windowTarget.elementOrSelector);
  referenceElement.appendChild(iframeElement);
  return iframeElement;
};

/** Valid values for the CSS `color-scheme` property, as used on iframe styles. */
export type CssColorScheme = 'light' | 'dark' | 'light dark' | '';

type IframeOptions = {
  permissions?: IframePermissions;
  colorScheme?: CssColorScheme;
};
/**
 * Creates and returns a fresh gwClientIframe on every call.
 * @returns a new gwClientIframe element
 */
export const getGwClientIframe = (options: IframeOptions = {}) => {
  const iframeEl = prepareGwClientIframe();
  setPermissions(iframeEl, options.permissions);
  // matches the page's own rendering (native form controls, scrollbars) to
  // the theme forwarded via the `theme` query param, before the page loads
  if (options.colorScheme) iframeEl.style.colorScheme = options.colorScheme;
  return iframeEl;
};

/** Load the client URL and reveal the iframe once the page has loaded. */
export const openUrlInIframe = (
  iframeElement: HTMLIFrameElement,
  url: string,
  options: { skipActiveElement?: boolean } = {},
) =>
  new Promise<void>((resolve) => {
    const iframeCopy = iframeElement;
    iframeCopy.onload = () => {
      iframeCopy.style.visibility = 'unset';
      resolve();
    };
    iframeCopy.setAttribute('src', url);
    // Allow layout while loading, but keep the iframe invisible behind the loader.
    iframeCopy.style.display = 'unset';
    if (!options.skipActiveElement) backupActiveElement();
    iframeCopy.style.overflow = 'hidden';
  });

/** remove the client from view  */
export const closeIframe = (
  iframeElement: HTMLIFrameElement,
  options: { skipActiveElement?: boolean } = {},
) => {
  const iframeCopy = iframeElement;
  iframeCopy.setAttribute('src', 'about:blank');
  iframeCopy.style.visibility = 'hidden';
  iframeCopy.style.display = 'none';
  if (!options.skipActiveElement) restoreActiveElement();
  iframeCopy.style.overflow = '';
  iframeCopy.parentNode?.removeChild(iframeCopy);
};
